import { Component, Input, ContentChild, TemplateRef, EventEmitter, Output, OnChanges, OnInit, SimpleChanges, ViewEncapsulation, HostListener, ElementRef } from '@angular/core';
import { Router } from '@angular/router';
import { Table, TableService, TableFilterEvent, TableLazyLoadEvent } from 'primeng/table';
import { TranslationService } from '../../../translation/translation.service';
import { TableColumn } from '../../interfaces/table.interface';
import { FormGroup } from '@angular/forms';
import { PaginatorState } from 'primeng/paginator';
import { SortEvent } from 'primeng/api';
import { BreakpointObserver, Breakpoints } from '@angular/cdk/layout';

type MobileField = { field: string; label?: string; type?: 'tag' | 'text' };

@Component({
    selector: 'app-table',
    standalone: false,
    templateUrl: './table.component.html',
    styleUrl: './table.component.scss',
    providers: [Table, TableService, TranslationService],
    encapsulation: ViewEncapsulation.None
})
export class TableComponent implements OnInit, OnChanges {
    constructor(
        private translationService: TranslationService,
        private router: Router,
        private bp: BreakpointObserver,
        private host: ElementRef<HTMLElement>
    ) { }

    @Input() totalRecords!: number | undefined;

    // ====== Inputs existentes ======
    @Input() column: TableColumn[] = [];
    @Input() data: any[] = [];
    @Input() loading = false;
    @Input() filterOptions: { [key: string]: any[] } = {};
    @Input() extraFiltersFormGroup!: FormGroup;
    @Input() otrasAcciones: boolean = false;
    @Input() accionesHeader: string = 'Acciones';
    @Input() tipoAccion: 'pdf' | 'acciones' = 'pdf';
    @Input() showFiltrosAvanzados = false;
    @Input() showFilterToggle = true;
    @Output() showFiltrosAvanzadosChange = new EventEmitter<boolean>();

    // ====== NUEVO: modo móvil opcional ======
    @Input() enableMobileList = false;
    @Input() mobileTitleField?: string;
    @Input() mobileFields: MobileField[] = [];
    @Input() mobileGroupByDateField?: string;
    @Input() mobileReuseActions = false;
    @Input() mobileCtaLabel?: string;
    @Input() mobileAccentBy?: string;
    @Input() mobileGroupMode: 'day' | 'relative' | 'month' = 'day';

    // Paginación móvil
    @Input() mobileRows: number = 10;
    @Input() mobileFirst: number = 0;

    @Input() responsiveMode: 'css' | 'breakpoint' = 'breakpoint';

    isMdUp = false;
    panelOpen = false;

    // ====== STATE ESTABLE PARA MÓVIL ======
    mobileFilteredData: any[] = [];
    mobilePageData: any[] = [];
    mobileGroupsView: { label: string; items: any[] }[] = [];

    get mobileTotal(): number {
        return this.mobileFilteredData.length;
    }
    get mobileLast(): number {
        return Math.min(this.mobileFirst + this.mobileRows, this.mobileTotal);
    }
    get mobileHasPrev(): boolean {
        return this.mobileFirst > 0;
    }
    get mobileHasNext(): boolean {
        return this.mobileLast < this.mobileTotal;
    }

    private formatFullDate(d: Date): string {
        const s = d.toLocaleDateString('es-AR', { weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
        return s.charAt(0).toUpperCase() + s.slice(1);
    }

    @ContentChild('mobileActions') mobileActionsTemplate?: TemplateRef<any>;

    @Output() limpiarFiltrosAvanzados = new EventEmitter<void>();
    @Output() limpiarFiltrosTabla = new EventEmitter<void>();
    @Output() onMobileCta = new EventEmitter<any>();
    @ContentChild('selectTemplate') selectTemplate!: TemplateRef<any>;
    @ContentChild('acciones', { static: false }) accionesTemplate?: TemplateRef<any>;
    @ContentChild('customCell') customCell!: TemplateRef<any>;

    @ContentChild('advancedFiltersTpl') advancedFiltersTpl?: TemplateRef<any>;

    onMobileActionClick(row: any, event: Event) {
        event.stopPropagation(); // Evita que se dispare el click de la fila si tienes uno
        this.onMobileCta.emit(row);
    }

    private _advOpen = false;
    get advOpen(): boolean {
        return this._advOpen;
    }
    toggleAdvanced() {
        this._advOpen = !this._advOpen;
    }

    first: number = 0;
    rows: number = 10;
    searchValue: string | undefined;
    selectedValue = null;
    sortField: string | undefined;
    sortOrder: number | undefined;
    totalVisible: number = 0;
    datosInternos: any[] = [];

    ngOnInit() {
        this.totalVisible = this.data.length;

        if (this.responsiveMode === 'breakpoint') {
            this.bp.observe([Breakpoints.Medium, Breakpoints.Large, Breakpoints.XLarge]).subscribe((state) => {
                this.isMdUp = state.matches;
            });
        }
        this.rebuildMobileView();
    }

    ngOnChanges(ch: SimpleChanges): void {
        if (ch['data']) {
            this.totalVisible = this.data.length;
            this.datosInternos = [...this.data];
            this.mobileFirst = 0;
        }
        if (ch['data'] || ch['mobileRows'] || ch['mobileGroupByDateField'] || ch['mobileGroupMode'] || ch['enableMobileList']) {
            this.rebuildMobileView();
        }
        if (ch['showFiltrosAvanzados']) {
            this.panelOpen = !!ch['showFiltrosAvanzados'].currentValue;
        }
    }

    // ====== Builder determinístico para la vista móvil ======
    rebuildMobileView(): void {
        if (!this.enableMobileList) {
            this.mobileFilteredData = [];
            this.mobilePageData = [];
            this.mobileGroupsView = [];
            return;
        }

        const value = (this.searchValue ?? '').toLowerCase().trim();
        const cols = this.globalFilterCol();

        const src = value ? this.data.filter((row) => cols.some((f) => ((row[f] ?? '') + '').toLowerCase().includes(value))) : this.data;

        this.mobileFilteredData = src;

        const last = Math.min(this.mobileFirst + this.mobileRows, this.mobileFilteredData.length);
        this.mobilePageData = this.mobileFilteredData.slice(this.mobileFirst, last);

        const groups = new Map<string, any[]>();
        const today = new Date();
        today.setHours(0, 0, 0, 0);
        const yesterday = new Date(today);
        yesterday.setDate(today.getDate() - 1);

        for (const row of this.mobilePageData) {
            let label = 'Sin fecha';

            if (this.mobileGroupByDateField) {
                const d = this.parseDate(row[this.mobileGroupByDateField]);
                const d0 = new Date(d);
                d0.setHours(0, 0, 0, 0);

                if (this.mobileGroupMode === 'relative') {
                    if (d0.getTime() === today.getTime()) label = 'Hoy';
                    else if (d0.getTime() === yesterday.getTime()) label = 'Ayer';
                    else label = d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
                } else if (this.mobileGroupMode === 'month') {
                    label = d.toLocaleDateString('es-AR', { month: 'long', year: 'numeric' });
                } else {
                    label = this.formatFullDate(d);
                }
            }

            if (!groups.has(label)) groups.set(label, []);
            groups.get(label)!.push(row);
        }

        this.mobileGroupsView = Array.from(groups, ([label, items]) => ({ label, items }));
    }

    // ====== Filtro eventos (tabla) ======
    private lastHadFilters = false;
    loadData(event: TableLazyLoadEvent) {
        const { filters, sortField, sortOrder } = event;

        // PrimeNG dispara un onLazyLoad inicial aun sin interacción del usuario.
        // Si no hay filtros reales ni sortField, ignoramos para no pisar el orden default
        // del componente padre ni disparar un request extra.
        const hasRealFilters = (() => {
            const f = (filters as any) ?? {};
            return Object.keys(f).some((k) => {
                if (k === 'global') return false;
                const filter = f[k];
                // PrimeNG puede devolver array o objeto directo
                const value = Array.isArray(filter) ? filter[0]?.value : filter?.value;
                return value !== null && value !== undefined && value !== '';
            });
        })();
        const hadFilters = this.lastHadFilters ?? false;
        this.lastHadFilters = hasRealFilters;

        if (!sortField && !hasRealFilters && !hadFilters) {
            return; // solo ignorar el disparo inicial real
        }

        this.onFilter({ filters: filters as any });
        this.onCustomSort({ field: sortField as any, order: sortOrder as any });
    }
    @Output() filter = new EventEmitter<{ name: string; query: string | string[] | null }[]>();
    onFilter(event: Partial<TableFilterEvent>) {
        const { filters } = event;

        const queries: { name: string; query: string | string[] | null }[] = [];

        for (let key in filters) {
            if (key !== 'global') {
                const filter = filters[key];
                let q: any;
                if (Array.isArray(filter)) q = filter[0].value;
                else q = filter?.value;

                const query = { name: key, query: q };
                queries.push(query);
            }
        }
        this.filter.emit(queries);
    }

    getTranslation(key: string): string {
        return this.translationService.getTranslation(key);
    }

    toggleFiltrosAvanzados() {
        this.panelOpen = !this.panelOpen;
        this.showFiltrosAvanzados = this.panelOpen;
        this.showFiltrosAvanzadosChange.emit(this.panelOpen);
    }

    globalFilterCol(): string[] {
        return this.column.map((c) => c.field as string);
    }

    clearFilters(table: Table) {
        table.clear();
        this.rows = 10;
        this.searchValue = '';
        this.mobileFirst = 0;
        this.limpiarFiltrosAvanzados.emit();
        this.limpiarFiltrosTabla.emit();
        this.rebuildMobileView();
    }

    @Output() globalFilter = new EventEmitter<{ global: string }>();
    onGlobalFilter(table: any, event: Event) {
        const value = (event.target as HTMLInputElement).value;
        this.globalFilter.emit({ global: value });
        this.searchValue = value;
        this.mobileFirst = 0;
        this.rebuildMobileView();
    }

    onMobileGlobalFilter(value: string) {
        const v = (value ?? '').toString();
        this.globalFilter.emit({ global: v });
        this.searchValue = v;
        this.mobileFirst = 0;
        this.rebuildMobileView();
    }

    stateTag(value: string): 'success' | 'warn' | 'danger' | 'info' | 'secondary' {
        // Normalizar a minúsculas, sin tildes y sin espacios redundantes
        const raw = (value ?? '').toString();
        const v = raw
            .toLowerCase()
            .normalize('NFD')
            .replace(/[\u0300-\u036f]/g, '') // quita diacríticos
            .trim();

        // 1) PRIORIDAD: estados negativos primero para evitar falsos positivos por substring
        if (
            v.includes('desaprob') ||   // "desaprobado/a"
            v.includes('rechaz') ||     // "rechazado/a"
            v.includes('deneg') ||      // "denegado/a"
            v.includes('no aprob') ||   // "no aprobado/a"
            v.includes('no autoriz') || // "no autorizado/a"
            v.includes('cancel') ||     // "cancelado/a"
            v.includes('anula') ||      // "anulado/a"
            v === 'abierto' ||
            v === 'abierta' ||
            v === 'vencido' ||
            v === 'inactivo' ||
            v === '0' ||
            v === 'no' ||
            v === 'extremo'
        ) {
            return 'danger';
        }

        // 2) Advertencias (amarillo)
        if (v === 'proximo a vencer' || v === 'alto') return 'warn';

        // 3) Positivos (verde)
        if (
            v === 'cerrado' ||
            v === 'vigente' ||
            v === 'autorizado' ||
            v === 'autorizada' ||
            v === 'activo' ||
            v === '1' ||
            v === 'si' ||
            v.includes('aprobado') || // "aprobado" | "aprobada"
            v.includes('aprobada')
        ) {
            return 'success';
        }

        // 4) Gris/neutral
        if (v === 'medio' || v === 'intermedio') return 'secondary';

        // 5) Azul/info por defecto (p.ej. "pendiente", "cerrada", etc.)
        return 'info';
    }

    @Output() customSort = new EventEmitter<{ sortByProperty: string; sortBy: 'asc' | 'desc' }>();
    onCustomSort(event: SortEvent) {
        const sortByProperty = event.field ?? 'id_visible';
        const sortBy = event.order == 1 ? 'asc' : 'desc';
        this.customSort.emit({ sortByProperty, sortBy });
    }

    @Output() pageChange = new EventEmitter<{ page: number; perPage: number }>();
    onPageChange(event: PaginatorState) {
        const page = (event.first ?? 0) / (event.rows ?? 10) + 1;
        const perPage = event.rows ?? 10;
        this.first = event.first ?? 0;
        this.rows = perPage;
        this.pageChange.emit({ page, perPage });
    }

    // ====== Helpers para la vista móvil ======
    private parseDate(v: any): Date {
        if (v instanceof Date) return v;
        if (typeof v === 'string' && /\d{2}\/\d{2}\/\d{4}/.test(v)) {
            const [fecha, hora = '00:00'] = v.split(' ');
            const [dd, mm, yyyy] = fecha.split('/');
            return new Date(`${yyyy}-${mm}-${dd}T${hora}`);
        }
        return new Date(v);
    }

    get mobileTitleKey() {
        return this.mobileTitleField ?? this.column?.[0]?.field ?? 'id';
    }

    getAccentClass(row: any): string {
        if (!this.mobileAccentBy) return 'mrow--info';
        const sev = this.stateTag(row[this.mobileAccentBy]);
        switch (sev) {
            case 'success':
                return 'mrow--success';
            case 'warn':
                return 'mrow--warn';
            case 'danger':
                return 'mrow--danger';
            case 'secondary':
                return 'mrow--secondary';
            default:
                return 'mrow--info';
        }
    }

    // Navegación/taps
    @Input() mobileRowLink?: (row: any) => string | any[] | void;
    @Output() mobileRowClick = new EventEmitter<any>();
    onMobileRowTap(row: any, evt: MouseEvent) {
        if (this.mobileRowLink) {
            const target = this.mobileRowLink(row);
            if (Array.isArray(target)) this.router.navigate(target);
            else if (typeof target === 'string') this.router.navigateByUrl(target);
            return;
        }
        this.mobileRowClick.emit(row);
    }

    @Input() desktopRowLink?: (row: any) => string | any[];
    @Output() desktopRowClick = new EventEmitter<any>();
    onDesktopRowTap(row: any, ev: MouseEvent) {
        const fn = this.desktopRowLink ?? this.mobileRowLink;
        if (fn) {
            const target = fn(row);
            if (Array.isArray(target)) this.router.navigate(target);
            else if (typeof target === 'string') this.router.navigateByUrl(target);
        } else {
            this.desktopRowClick.emit(row);
        }
    }

    // Paginación móvil - acciones
    mobilePrevPage(): void {
        if (!this.mobileHasPrev) return;
        this.mobileFirst = Math.max(0, this.mobileFirst - this.mobileRows);
        this.rebuildMobileView();
    }
    mobileNextPage(): void {
        if (!this.mobileHasNext) return;
        this.mobileFirst = Math.min(Math.max(0, this.mobileTotal - 1), this.mobileFirst + this.mobileRows);
        this.rebuildMobileView();
    }

    getField(field: string | number | symbol): string | undefined {
        return field as string;
    }

    /** Cerrar programáticamente */
    closeAdvanced() {
        if (!this.panelOpen) return;
        this.panelOpen = false;
        this.showFiltrosAvanzados = false;
        this.showFiltrosAvanzadosChange.emit(false);
    }

    // === Cierre con click afuera / ESC ===
    @HostListener('document:touchstart', ['$event'])
    onTouchStart(ev: TouchEvent) {
        if (!this.panelOpen) return;
        const target = ev.target as Node;
        const panel = this.host.nativeElement.querySelector('.advanced-panel');
        const toggles = this.host.nativeElement.querySelectorAll('.adv-toggle-btn');
        const insidePanel = !!panel && panel.contains(target);
        const onToggle = Array.from(toggles).some((b) => b.contains(target));
        if (!insidePanel && !onToggle) this.closeAdvanced();
    }

    @HostListener('document:click', ['$event'])
    onDocumentClick(ev: MouseEvent) {
        if (!this.panelOpen) return;

        const target = ev.target as Node;
        const panel = this.host.nativeElement.querySelector('.advanced-panel');
        const toggles = this.host.nativeElement.querySelectorAll('.adv-toggle-btn');

        const insideOverlay = (target as HTMLElement).closest('.p-datepicker, .p-dropdown-panel, .p-multiselect-panel, .p-overlaypanel, .p-dialog, [data-pc-section], [data-pc-name]');

        const insidePanel = !!panel && panel.contains(target);
        const onToggle = Array.from(toggles).some((b) => b.contains(target));

        if (!insidePanel && !onToggle && !insideOverlay) this.closeAdvanced();
    }

    @HostListener('document:keydown.escape')
    onEsc() {
        this.closeAdvanced();
    }
}
